import { Component, effect, inject, input, signal } from '@angular/core';
import { takeUntilDestroyed, toObservable, toSignal } from '@angular/core/rxjs-interop';
import { FormArray, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatOption, MatSelect } from '@angular/material/select';
import { MatButton, MatIconButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { MatInput } from '@angular/material/input';
import { combineLatest, forkJoin, Observable, of, shareReplay, switchMap, take, tap } from 'rxjs';
import { TranslateModule } from '@ngx-translate/core';
import { GalaxyFormFieldModule } from '@vendasta/galaxy/form-field';
import { CrmInjectionToken, CrmObjectService, FieldValueInterface } from '@galaxy/crm/static';
import { NgxMatSelectSearchModule } from 'ngx-mat-select-search';
import { InfiniteScrollTriggerComponent } from '@vendasta/galaxy/infinite-scroll-trigger';
import {
  CRMFieldSchemaCustomizationApiService,
  FieldSchemaInterface,
  FieldType,
  FilterGroupOperator,
  FilterOperator,
  Pipeline,
  PipelinesApiService,
} from '@vendasta/crm';
import { GalaxyLoadingSpinnerModule } from '@vendasta/galaxy/loading-spinner';
import { SnackbarService } from '@vendasta/galaxy/snackbar-service';
import { GalaxyStickyFooterModule } from '@vendasta/galaxy/sticky-footer';
import { catchError, map, withLatestFrom } from 'rxjs/operators';

export interface FieldSchemaWithRestrictions {
  schema: FieldSchemaInterface;
  restrictedValues: FieldValueInterface[];
}

@Component({
  selector: 'crm-board-settings',
  templateUrl: './board-settings.component.html',
  styleUrl: './board-settings.component.scss',
  imports: [
    GalaxyFormFieldModule,
    MatSelect,
    MatOption,
    MatButton,
    MatIconButton,
    MatIcon,
    MatInput,
    TranslateModule,
    ReactiveFormsModule,
    NgxMatSelectSearchModule,
    InfiniteScrollTriggerComponent,
    GalaxyLoadingSpinnerModule,
    GalaxyStickyFooterModule,
  ],
})
export class BoardSettingsComponent {
  readonly objectSubtype = input<string>('');
  readonly objectType = input<string>('CustomObject');

  private readonly crmObjectService = inject(CrmObjectService);
  private readonly snackbarService = inject(SnackbarService);
  private readonly pipelinesApiService = inject(PipelinesApiService);
  private readonly crmInjectionConfig = inject(CrmInjectionToken);
  private readonly crmFieldCustomizationApiService = inject(CRMFieldSchemaCustomizationApiService);

  protected readonly pipelinesForm = new FormArray<FormGroup>([]);
  protected readonly searchControl = new FormControl<string>('', { nonNullable: true });
  protected readonly searchValue = toSignal(this.searchControl.valueChanges, {
    initialValue: '',
  });
  protected readonly hasMore = signal(true);
  private readonly fetchMore = signal(true);
  private readonly cursor = signal('');
  protected readonly fieldSchemaOptions = signal<Map<string, FieldSchemaWithRestrictions>>(new Map());
  protected readonly fieldsLoading = signal(false);
  protected readonly saving = signal(false);
  protected readonly existingPipelinesLoading = signal(false);
  private readonly existingPipelineStages = signal(new Map<string, string[]>());

  protected isExistingPipeline(group: FormGroup): boolean {
    return !!(group.get('pipelineId')?.value as string);
  }

  private listFieldOptions$() {
    return combineLatest([
      toObservable(this.objectSubtype),
      toObservable(this.objectType),
      this.searchControl.valueChanges,
      toObservable(this.fetchMore),
    ]).pipe(
      tap(() => this.fieldsLoading.set(true)),
      switchMap(([subtype, objectType, searchTerm]) => {
        return this.crmObjectService.listFieldSchemas(
          objectType,
          subtype,
          { pageSize: 100, cursor: this.cursor() },
          searchTerm,
        );
      }),
      withLatestFrom(this.crmInjectionConfig.namespace$),
      switchMap(([listResp, namespace]) => {
        const fieldIds = (listResp.fieldSchemas ?? []).map((s) => s.fieldId).filter((id) => id);
        if (!fieldIds.length) {
          return of({ filtered: [], pagingMetadata: listResp.pagingMetadata });
        }
        return this.crmFieldCustomizationApiService //All custom object field schema customizations are saved under 'CustomObject'
          .getMultiFieldSchemaCustomization({
            namespace,
            crmObjectType: this.objectType(),
            fieldId: fieldIds as string[],
          })
          .pipe(
            map((customResp) => {
              const customMap = new Map(
                (customResp?.fieldSchemaCustomizations ?? []).map((c) => [c.fieldId, c.restrictedFieldValues ?? []]),
              );
              return {
                filtered: (listResp.fieldSchemas ?? [])
                  .filter(
                    //filter down to string fields with restricted values
                    (s) =>
                      s.fieldId &&
                      s.fieldType === FieldType.FIELD_TYPE_STRING &&
                      (customMap.get(s.fieldId) ?? []).length > 0,
                  )
                  .map((s) => ({ schema: s, restrictedValues: customMap.get(s.fieldId ?? '') ?? [] })),
                pagingMetadata: listResp.pagingMetadata,
              };
            }),
          );
      }),
      catchError((_) => {
        this.snackbarService.openErrorSnack('CUSTOM_FIELDS.LABELS.FAILED_TO_LOAD_FIELDS');
        return of({ filtered: [], pagingMetadata: { nextCursor: '', hasMore: false } });
      }),
      tap(() => this.fieldsLoading.set(false)),
      shareReplay(1),
    );
  }

  constructor() {
    effect(() => {
      this.objectSubtype(); //reset search results when object subtype changes
      this.cursor.set('');
      this.fieldSchemaOptions.update((current) => {
        const preserved = new Map<string, FieldSchemaWithRestrictions>();
        this.getSelectedFieldIds().forEach((id) => {
          const entry = current.get(id);
          if (entry) preserved.set(id, entry);
        });
        return preserved;
      });
    });

    effect(() => {
      this.searchValue(); //reset search results when search term changes
      this.cursor.set('');
      this.fieldSchemaOptions.update((current) => {
        const preserved = new Map<string, FieldSchemaWithRestrictions>();
        this.getSelectedFieldIds().forEach((id) => {
          const entry = current.get(id);
          if (entry) preserved.set(id, entry);
        });
        return preserved;
      });
    });

    this.listFieldOptions$()
      .pipe(takeUntilDestroyed())
      .subscribe((resp) => {
        this.cursor.set(resp.pagingMetadata?.nextCursor ?? '');
        this.hasMore.set(resp.pagingMetadata?.hasMore ?? false);
        this.fieldSchemaOptions.update((current) => {
          const next = new Map(current);
          resp.filtered.forEach((f: FieldSchemaWithRestrictions) => {
            if (f.schema.fieldId) next.set(f.schema.fieldId, f);
          });
          return next;
        });
        this.fetchMore.set(false);
      });

    const existingPipelines$ = combineLatest(
      [this.crmInjectionConfig.namespace$,
        toObservable(this.objectType),
        toObservable(this.objectSubtype)]).pipe(
      tap(() => this.existingPipelinesLoading.set(true)),
      switchMap(([namespace, objectType, subtype]) =>
        this.pipelinesApiService.listPipelines({
          listOptions: {
            namespace,
            objectType: objectType,
            crmObjectSubtype: subtype,
          },
        }),
      ),
      map((resp) => resp.pipelines),
      catchError(() => {
        this.snackbarService.openErrorSnack('CUSTOM_FIELDS.LABELS.FAILED_TO_LOAD_FIELDS');
        return of([] as Pipeline[]);
      }),
      takeUntilDestroyed(),
      shareReplay(1),
    );

    existingPipelines$.subscribe((pipelines) => {
      (pipelines ?? []).forEach((pipeline) => this.addFormForExistingPipeline(pipeline));
      this.existingPipelinesLoading.set(false);
    });

    const fieldsOnExistingPipelines$ = existingPipelines$.pipe(
      switchMap((pipelines) => {
        const fieldIds = pipelines
          .map((p) => p.stages?.[0]?.filterGroup?.filters?.[0]?.fieldId)
          .filter((id): id is string => !!id);
        if (!fieldIds.length) return of(null);
        return this.crmObjectService.getMultiFieldSchema(this.objectType(), fieldIds, this.objectSubtype());
      }),
      map((resp) => resp?.fieldSchemas ?? []),
      shareReplay(1),
    );

    //need to add support for what current pipelines should have selected in the field form
    const fieldOptionsFromExistingPipelines$ = combineLatest([
      fieldsOnExistingPipelines$,
      this.crmInjectionConfig.namespace$,
    ]).pipe(
      switchMap(([pipelineFields, namespace]) => {
        const fieldIds = pipelineFields.filter((s) => !!s.fieldId).map((s) => s.fieldId as string);
        if (!fieldIds.length) return of(null);
        return this.crmFieldCustomizationApiService.getMultiFieldSchemaCustomization({
          namespace,
          crmObjectType: this.objectType(),
          fieldId: fieldIds,
        });
      }),
      map((resp) => resp?.fieldSchemaCustomizations ?? []),
      withLatestFrom(fieldsOnExistingPipelines$),
      map(([customizations, fields]) => {
        const customMap = new Map(customizations.map((c) => [c.fieldId, c.restrictedFieldValues ?? []]));
        const newOptions = new Map<string, FieldSchemaWithRestrictions>();
        fields
          .filter(
            (s) =>
              s.fieldId && s.fieldType === FieldType.FIELD_TYPE_STRING && (customMap.get(s.fieldId) ?? []).length > 0,
          )
          .forEach((s) => {
            newOptions.set(s.fieldId ?? '', {
              schema: s,
              restrictedValues: customMap.get(s.fieldId ?? '') ?? [],
            });
          });
        return newOptions;
      }),
      catchError(() => of(new Map<string, FieldSchemaWithRestrictions>())),
      takeUntilDestroyed(),
      shareReplay(1),
    );
    fieldOptionsFromExistingPipelines$.subscribe((existingFieldOptions) => {
      this.fieldSchemaOptions.update((current) => {
        const next = new Map(current);
        existingFieldOptions.forEach((value, key) => {
          if (!next.has(key)) next.set(key, value);
        });
        return next;
      });
    });
  }

  protected addPipeline(): void {
    this.pipelinesForm.push(
      new FormGroup({
        pipelineId: new FormControl<string>('', { nonNullable: true }),
        name: new FormControl<string>('', { nonNullable: true, validators: [Validators.required] }),
        fieldId: new FormControl<string | null>(null, { validators: [Validators.required] }),
      }),
    );
  }

  protected deletePipeline(index: number): void {
    const group = this.pipelinesForm.at(index);
    const pipelineId = group.get('pipelineId')?.value as string;
    if (pipelineId) {
      this.deleteExistingPipeline(pipelineId, index);
    } else {
      this.pipelinesForm.removeAt(index);
    }
  }

  private deleteExistingPipeline(pipelineId: string, index: number) {
    this.crmInjectionConfig.namespace$
      .pipe(
        take(1),
        switchMap((namespace) => this.pipelinesApiService.deletePipeline({ namespace, pipelineId })),
      )
      .subscribe({
        next: () => {
          this.pipelinesForm.removeAt(index);
          this.existingPipelineStages.update((m) => {
            const next = new Map(m);
            next.delete(pipelineId);
            return next;
          });
          this.snackbarService.openSuccessSnack('CUSTOM_FIELDS.LABELS.SAVE_SUCCESS');
        },
        error: () => {
          this.snackbarService.openErrorSnack('CUSTOM_FIELDS.LABELS.SAVE_FAILED');
        },
      });
  }

  protected getStageNames(group: FormGroup): string[] {
    if (this.isExistingPipeline(group)) {
      const pipelineId = group.get('pipelineId')?.value as string;
      return this.existingPipelineStages().get(pipelineId) ?? [];
    }
    return this.getFieldOptions(group);
  }

  private addFormForExistingPipeline(pipeline: Pipeline): void {
    //a bit hacky but right now all custom obj pipelines are only based on one field id
    const fieldId = pipeline.stages?.[0]?.filterGroup?.filters?.[0]?.fieldId ?? null;
    this.pipelinesForm.push(
      new FormGroup({
        pipelineId: new FormControl<string>(pipeline.pipelineId ?? '', { nonNullable: true }),
        name: new FormControl<string>(pipeline.name ?? '', { nonNullable: true, validators: [Validators.required] }),
        fieldId: new FormControl<string | null>(fieldId, { validators: [Validators.required] }),
      }),
    );
    if (pipeline.pipelineId) {
      this.existingPipelineStages.update((m) => {
        const next = new Map(m);
        const stageNames = (pipeline.stages ?? []).map((s) => s.name ?? '').filter(Boolean);
        next.set(pipeline.pipelineId, stageNames);
        return next;
      });
    }
  }

  protected onSelectOpened(group: FormGroup): void {
    this.searchControl.setValue('');
    group.get('fieldId')?.markAsDirty();
  }

  protected showNewStages(group: FormGroup): boolean {
    if (this.isExistingPipeline(group)) {
      return group.get('fieldId')?.dirty === true && this.getFieldOptions(group).length > 0;
    }
    return this.getFieldOptions(group).length > 0;
  }

  protected loadMore() {
    this.fetchMore.set(true);
  }

  protected get canSave(): boolean {
    if (!this.pipelinesForm.dirty) return false;
    return this.pipelinesForm.valid;
  }

  protected getFieldOptions(group: FormGroup): string[] {
    const fieldId = group.get('fieldId')?.value;
    if (!fieldId) return [];
    const field = this.fieldSchemaOptions().get(fieldId);
    //only supporting fields with string values right now
    return (field?.restrictedValues ?? []).map((rv) => rv.stringValue ?? '').filter(Boolean);
  }

  private getSelectedFieldIds(): string[] {
    return this.pipelinesForm.controls.map((g) => g.get('fieldId')?.value).filter((id): id is string => !!id);
  }

  private buildCreateRequest(group: FormGroup, namespace: string): Observable<boolean> {
    const name = group.controls['name'].value as string;
    const fieldId = group.controls['fieldId'].value as string;
    const restrictedValues = this.fieldSchemaOptions().get(fieldId)?.restrictedValues ?? [];
    const stages = restrictedValues.map((rv) => {
      const value = rv.stringValue ?? '';
      return {
        name: value,
        filterGroup: {
          operator: FilterGroupOperator.FILTER_GROUP_OPERATOR_AND,
          filters: [{ fieldId, operator: FilterOperator.FILTER_OPERATOR_IS, values: [{ string: value }] }],
        },
        mutations: [{ fieldId, stringValue: value }],
      };
    });
    return this.pipelinesApiService
      .createPipeline({
        pipeline: { namespace, objectType: this.objectType(), crmObjectSubtype: this.objectSubtype(), name, stages },
      })
      .pipe(
        tap((res) => {
          const newPipelineId = res.pipelineId ?? '';
          group.controls['pipelineId'].setValue(newPipelineId);
          if (newPipelineId) {
            const stageNames = stages.map((s) => s.name).filter(Boolean);
            this.existingPipelineStages.update((m) => {
              const next = new Map(m);
              next.set(newPipelineId, stageNames);
              return next;
            });
          }
        }),
        map(() => true as boolean),
        catchError(() => of(false)),
      );
  }

  private buildUpdateRequest(group: FormGroup, namespace: string): Observable<boolean> {
    const pipelineId = group.controls['pipelineId'].value as string;
    const name = group.controls['name'].value as string;
    const fieldIdControl = group.get('fieldId');
    const fieldIdIsDirty = fieldIdControl?.dirty === true;

    if (fieldIdIsDirty) {
      const fieldId = fieldIdControl?.value as string;
      const restrictedValues = this.fieldSchemaOptions().get(fieldId)?.restrictedValues ?? [];
      const stages = restrictedValues.map((rv) => {
        const value = rv.stringValue ?? '';
        return {
          name: value,
          filterGroup: {
            operator: FilterGroupOperator.FILTER_GROUP_OPERATOR_AND,
            filters: [{ fieldId, operator: FilterOperator.FILTER_OPERATOR_IS, values: [{ string: value }] }],
          },
          mutations: [{ fieldId, stringValue: value }],
        };
      });
      return this.pipelinesApiService
        .updatePipeline({
          pipeline: {
            pipelineId,
            name,
            stages,
            namespace,
            objectType: this.objectType(),
            crmObjectSubtype: this.objectSubtype(),
          },
          fieldMask: { paths: ['name', 'stages'] },
        })
        .pipe(
          tap(() => {
            this.existingPipelineStages.update((m) => {
              const next = new Map(m);
              next.set(pipelineId, stages.map((s) => s.name).filter(Boolean));
              return next;
            });
          }),
          map(() => true as boolean),
          catchError(() => of(false)),
        );
    }

    return this.pipelinesApiService
      .updatePipeline({
        pipeline: { pipelineId, name, namespace },
        fieldMask: { paths: ['name'] },
      })
      .pipe(
        map(() => true as boolean),
        catchError(() => of(false)),
      );
  }

  protected onSaveClick(): void {
    if (!this.canSave) {
      this.snackbarService.openErrorSnack('CUSTOM_FIELDS.LABELS.REQUIRED_FIELDS');
      return;
    }
    this.saving.set(true);
    this.crmInjectionConfig.namespace$
      .pipe(
        take(1),
        switchMap((namespace) => {
          const requests$ = this.pipelinesForm.controls
            .filter((g) => g.dirty)
            .map((group) =>
              this.isExistingPipeline(group)
                ? this.buildUpdateRequest(group, namespace)
                : this.buildCreateRequest(group, namespace),
            );
          return forkJoin(requests$);
        }),
      )
      .subscribe({
        next: (results) => {
          this.saving.set(false);
          if (results.every((r) => r)) {
            this.pipelinesForm.controls.forEach((g) => g.markAsPristine());
            this.snackbarService.openSuccessSnack('CUSTOM_FIELDS.LABELS.SAVE_SUCCESS');
          } else {
            this.snackbarService.openErrorSnack('CUSTOM_FIELDS.LABELS.SAVE_FAILED');
          }
        },
        error: () => {
          this.saving.set(false);
          this.snackbarService.openErrorSnack('CUSTOM_FIELDS.LABELS.SAVE_FAILED');
        },
      });
  }
}
